import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { User, Lock, LogIn, Eye, EyeOff, Landmark } from 'lucide-react';

import { cn } from '@/lib/cn';
import { useAuthStore } from '@/stores/auth.store';
import { authApi } from '@/services/api/auth';
import { getErrorMessage } from '@/utils/error';

const loginSchema = z.object({
  username: z.string().min(1, 'Username is required'),
  password: z.string().min(4, 'Password must be at least 4 characters'),
  rememberMe: z.boolean().optional(),
});

type LoginFormValues = z.infer<typeof loginSchema>;

export function LoginPage() {
  const navigate = useNavigate();
  const login = useAuthStore((state) => state.login);
  const [showPassword, setShowPassword] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LoginFormValues>({
    resolver: zodResolver(loginSchema),
    defaultValues: { rememberMe: false },
  });

  const onSubmit = async (data: LoginFormValues) => {
    try {
      setServerError(null);
      const res = await authApi.login({
        username: data.username,
        password: data.password,
        rememberMe: data.rememberMe,
      });
      login(res.data.data.accessToken);
      navigate('/dashboard/profile');
    } catch (err) {
      setServerError(getErrorMessage(err));
    }
  };

  return (
    <>
      {/* Header Brand */}
      <div className="mb-10 text-center">
        <div className="mb-2 inline-flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-dark-primary-container">
            <Landmark className="h-5 w-5 text-white" />
          </div>
          <h1 className="font-headline text-2xl font-extrabold tracking-tighter text-dark-on-surface">
            Architect <span className="text-dark-secondary-container">Admin</span>
          </h1>
        </div>
        <p className="font-sans text-[10px] uppercase tracking-[0.2em] text-dark-outline">
          Technical Mastery Protocol v2.4.0
        </p>
      </div>

      {/* Login Card */}
      <section className="rounded-xl border-l border-t border-dark-outline-variant/15 bg-dark-surface-container-low p-8 shadow-[0_40px_60px_-15px_rgba(0,26,64,0.4)]">
        <header className="mb-8">
          <h2 className="font-headline text-xl font-bold">Access Terminal</h2>
          <p className="text-sm text-dark-on-surface-variant">
            Authorized personnel only. Enter credentials.
          </p>
        </header>

        {serverError && (
          <div className="mb-6 rounded-lg border border-error/30 bg-error/10 px-4 py-3 text-sm text-error">
            {serverError}
          </div>
        )}

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          {/* Username */}
          <div className="space-y-2">
            <label
              htmlFor="username"
              className="ml-1 block text-[10px] font-medium uppercase tracking-widest text-dark-outline"
            >
              Operator Identification
            </label>
            <div className="group relative">
              <User className="absolute left-4 top-1/2 h-[18px] w-[18px] -translate-y-1/2 text-dark-outline transition-colors group-focus-within:text-dark-secondary-container" />
              <input
                id="username"
                type="text"
                placeholder="username_id"
                className={cn(
                  'w-full rounded-lg border border-dark-outline-variant/30 bg-dark-surface-container-lowest py-3.5 pl-12 pr-4 text-sm text-dark-on-surface transition-all placeholder:text-dark-outline/40 focus:border-dark-primary-container focus:outline-none focus:ring-1 focus:ring-dark-primary-container',
                  errors.username &&
                    'border-error focus:border-error focus:ring-error',
                )}
                {...register('username')}
              />
            </div>
            {errors.username && (
              <p className="ml-1 text-xs text-error">{errors.username.message}</p>
            )}
          </div>

          {/* Password */}
          <div className="space-y-2">
            <div className="flex items-end justify-between px-1">
              <label
                htmlFor="password"
                className="block text-[10px] font-medium uppercase tracking-widest text-dark-outline"
              >
                Security Cipher
              </label>
              <a
                href="#"
                className="text-[10px] uppercase tracking-widest text-dark-secondary transition-colors hover:text-white"
              >
                Recover?
              </a>
            </div>
            <div className="group relative">
              <Lock className="absolute left-4 top-1/2 h-[18px] w-[18px] -translate-y-1/2 text-dark-outline transition-colors group-focus-within:text-dark-secondary-container" />
              <input
                id="password"
                type={showPassword ? 'text' : 'password'}
                placeholder="••••••••••••"
                className={cn(
                  'w-full rounded-lg border border-dark-outline-variant/30 bg-dark-surface-container-lowest py-3.5 pl-12 pr-11 text-sm text-dark-on-surface transition-all placeholder:text-dark-outline/40 focus:border-dark-primary-container focus:outline-none focus:ring-1 focus:ring-dark-primary-container',
                  errors.password &&
                    'border-error focus:border-error focus:ring-error',
                )}
                {...register('password')}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-dark-outline transition-colors hover:text-dark-on-surface"
                tabIndex={-1}
              >
                {showPassword ? (
                  <EyeOff className="h-[18px] w-[18px]" />
                ) : (
                  <Eye className="h-[18px] w-[18px]" />
                )}
              </button>
            </div>
            {errors.password && (
              <p className="ml-1 text-xs text-error">{errors.password.message}</p>
            )}
          </div>

          {/* Remember Me */}
          <div className="flex items-center gap-2 px-1">
            <input
              id="persist"
              type="checkbox"
              className="h-4 w-4 cursor-pointer rounded border-dark-outline-variant/30 bg-dark-surface-container-lowest text-dark-primary-container focus:ring-dark-primary-container"
              {...register('rememberMe')}
            />
            <label
              htmlFor="persist"
              className="cursor-pointer text-xs text-dark-on-surface-variant"
            >
              Maintain session across deployments
            </label>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={isSubmitting}
            className="flex w-full items-center justify-center gap-2 rounded-full bg-gradient-to-r from-dark-primary-container to-dark-secondary-container py-4 font-headline font-bold text-white shadow-[0_10px_20px_-5px_rgba(0,86,179,0.4)] transition-all hover:brightness-110 active:scale-[0.98] disabled:pointer-events-none disabled:opacity-50"
          >
            {isSubmitting ? 'Authenticating...' : 'Initiate Authentication'}
            <LogIn className="h-5 w-5" />
          </button>
        </form>

        {/* Technical Metadata */}
        <div className="mt-8 flex flex-col gap-3 border-t border-dark-outline-variant/10 pt-6">
          <div className="flex items-center justify-between text-[10px] uppercase tracking-tighter text-dark-outline">
            <span className="flex items-center gap-1.5">
              <span className="h-1.5 w-1.5 rounded-full bg-green-500" />
              System: Active
            </span>
            <span>Encrypted: AES-256</span>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="mt-12 flex w-full flex-col items-center gap-4 px-8">
        <div className="flex gap-6">
          <a
            href="#"
            className="text-[10px] uppercase tracking-widest text-dark-outline-variant transition-colors hover:text-dark-on-surface"
          >
            Support
          </a>
          <a
            href="#"
            className="text-[10px] uppercase tracking-widest text-dark-outline-variant transition-colors hover:text-dark-on-surface"
          >
            API Docs
          </a>
          <a
            href="#"
            className="text-[10px] uppercase tracking-widest text-dark-outline-variant transition-colors hover:text-dark-on-surface"
          >
            Privacy
          </a>
        </div>
        <p className="text-center text-[10px] uppercase tracking-widest text-dark-outline-variant">
          &copy; 2024 Precision Engineer Admin. Technical Mastery Protocol.
        </p>
      </footer>
    </>
  );
}
